import React, { useMemo, useState } from "react";

import _ from "lodash";

import {
  copyToClipboard,
  hasAdminTierEqualOrAbove,
  incomplete,
  useAdminUserContext,
  useDeleteCarbonProgramDraft,
  useGetAllCarbonPrograms,
  useGetCarbonProgramDraft,
  useGetPartners,
  useGetPracticeCrops,
  useMarkCarbonProgramDraftImplemented,
  useNavigate,
  useParams,
  useReviewCarbonProgramDraft,
  useSubmitCarbonProgramDraft,
  useUpdateCarbonProgramDraft,
  useUploadCarbonProgramDraftContract,
} from "utils";

import { CARBON_PROGRAM_DRAFT_EDITABLE_STATUSES, CARBON_PROGRAM_DRAFT_KIND, CARBON_PROGRAM_DRAFT_STATUS, CarbonProgramDraftDetails, Practice } from "models";

import {
  Alert,
  Button,
  Flex,
  Form,
  Link,
  MainPage,
  ModalContent,
  PageSpin,
  Table as TableJs,
  TextArea,
  Typography,
  Upload,
  Message as message,
} from "components";

import { DraftForm } from "pages/admin/carbonPrograms/drafts/DraftForm";
import { DraftFormValues, configToFormValues, diffConfigs, formValuesToConfig } from "pages/admin/carbonPrograms/drafts/draftConfig";
import { DraftStatusTag, adminUserName, draftKindLabel, draftProgramName, formatDraftDate } from "pages/admin/carbonPrograms/drafts/draftDisplay";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Table = TableJs as React.FC<any>;

const formatSetting = (value: unknown) => (value === undefined ? "Not set" : typeof value === "string" ? value : JSON.stringify(value));

const IssueList = ({ draft }: { draft: CarbonProgramDraftDetails }) => {
  const errors = draft.issues.filter((issue) => issue.severity === "error");
  const warnings = draft.issues.filter((issue) => issue.severity === "warning");
  const issueList = (issues: CarbonProgramDraftDetails["issues"]) => (
    <ul>
      {issues.map((issue) => (
        <li key={`${issue.path}-${issue.message}`}>{issue.path ? `${issue.path}: ${issue.message}` : issue.message}</li>
      ))}
    </ul>
  );

  return (
    <Flex vertical gap={8}>
      {errors.length > 0 && (
        <Alert
          className=""
          closable={false}
          type="red"
          headline={`${errors.length} problem${errors.length === 1 ? "" : "s"} to fix before submitting`}
          description={issueList(errors)}
        />
      )}
      {warnings.length > 0 && <Alert className="" closable={false} type="warning" headline="Worth a second look" description={issueList(warnings)} />}
    </Flex>
  );
};

const CarbonProgramDraftPage = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const { user } = useAdminUserContext();
  const [form] = Form.useForm<DraftFormValues>();

  const draftQuery = useGetCarbonProgramDraft(id);
  const { data: livePrograms = [] } = useGetAllCarbonPrograms();
  const partnersQuery = useGetPartners();
  const cropsQuery = useGetPracticeCrops({ enabled: true, isAdmin: true });

  const updateMutation = useUpdateCarbonProgramDraft();
  const submitMutation = useSubmitCarbonProgramDraft();
  const reviewMutation = useReviewCarbonProgramDraft();
  const implementedMutation = useMarkCarbonProgramDraftImplemented();
  const deleteMutation = useDeleteCarbonProgramDraft();
  const uploadContractMutation = useUploadCarbonProgramDraftContract();

  const [isRejecting, setIsRejecting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [rejectComment, setRejectComment] = useState("");

  const draft = draftQuery.data;
  const initialValues = useMemo(() => (draft ? configToFormValues(draft.config) : {}), [draft]);
  const liveProgram = livePrograms.find((program) => program.id === draft?.programId);
  const changes = useMemo(() => {
    if (!draft || draft.kind !== CARBON_PROGRAM_DRAFT_KIND.update || !liveProgram) return [];
    const { farmCount: _farmCount, ...liveConfig } = liveProgram;
    return diffConfigs(liveConfig, draft.config);
  }, [draft, liveProgram]);

  if (draftQuery.isLoading) return <PageSpin />;
  if (!draft) return <MainPage pageName="Carbon Program Draft">Draft not found.</MainPage>;

  const isEditable = CARBON_PROGRAM_DRAFT_EDITABLE_STATUSES.includes(draft.status);
  const isReviewer = hasAdminTierEqualOrAbove(user?.role, "tier2");
  const hasErrors = draft.issues.some((issue) => issue.severity === "error");
  const isBusy = updateMutation.isPending || submitMutation.isPending || reviewMutation.isPending || implementedMutation.isPending;

  const save = async () => {
    const values = await form.validateFields().catch(() => null);
    if (!values) {
      incomplete();
      return null;
    }
    return updateMutation.mutateAsync({
      id: draft.id,
      config: formValuesToConfig(values, draft.config, livePrograms),
      notes: (values.notes as string | undefined) || null,
    });
  };

  const handleSave = async () => {
    if (await save()) message({ content: "Draft saved", icon: "tick" });
  };

  const handleSubmit = async () => {
    const saved = await save();
    if (!saved) return;
    if (saved.issues.some((issue) => issue.severity === "error")) {
      message({ content: "Fix the problems listed at the top before submitting", icon: "error" });
      return;
    }
    submitMutation.mutate(draft.id, { onSuccess: () => message({ content: "Submitted for review", icon: "tick" }) });
  };

  const handleReject = () => reviewMutation.mutate({ id: draft.id, decision: "reject", comment: rejectComment }, { onSuccess: () => setIsRejecting(false) });

  const handleDelete = () => deleteMutation.mutate(draft.id, { onSuccess: () => navigate("/carbon-programs/drafts") });

  const handleCopyConfig = async () => {
    await copyToClipboard(JSON.stringify(draft.config, null, 2));
    message({ content: "Config copied", icon: "tick" });
  };

  return (
    <MainPage pageName="Carbon Program Draft">
      <Flex vertical gap={24}>
        <Flex vertical gap={8}>
          <Link onClick={() => navigate("/carbon-programs/drafts")}>Back to drafts</Link>
          <Flex gap={16} align="center" wrap="wrap">
            <Typography variant="h2">{draftProgramName(draft)}</Typography>
            <DraftStatusTag status={draft.status} />
          </Flex>
          <Typography variant="paragraph-m" color="grey-600">
            {draftKindLabel(draft)}
            {draft.kind === CARBON_PROGRAM_DRAFT_KIND.update && liveProgram && ` (${liveProgram.internalName || liveProgram.programName})`} · Requested by{" "}
            {adminUserName(draft.createdBy)} · Last updated {formatDraftDate(draft.updatedAt)}
          </Typography>
        </Flex>

        {draft.status === CARBON_PROGRAM_DRAFT_STATUS.rejected && (
          <Alert
            className=""
            closable={false}
            type="red"
            headline={`Changes requested by ${adminUserName(draft.reviewedBy)}`}
            description={draft.reviewComment ?? ""}
          />
        )}
        {draft.status === CARBON_PROGRAM_DRAFT_STATUS.submitted && (
          <Alert
            className=""
            closable={false}
            type="warning"
            headline="Waiting for review"
            description="Product & engineering will approve the draft or ask for changes."
          />
        )}
        {draft.status === CARBON_PROGRAM_DRAFT_STATUS.approved && (
          <Alert
            className=""
            closable={false}
            type="green"
            headline={`Approved by ${adminUserName(draft.reviewedBy)}`}
            description={
              draft.kind === CARBON_PROGRAM_DRAFT_KIND.create
                ? `To roll out: add the config (Copy config) to consts/carbonPrograms.ts with id ${draft.programId}, deploy, then mark it as live.`
                : "To roll out: apply the changes below to consts/carbonPrograms.ts, deploy, then mark it as live."
            }
          />
        )}
        {draft.status === CARBON_PROGRAM_DRAFT_STATUS.implemented && (
          <Alert
            className=""
            closable={false}
            type="green"
            headline={`Live since ${formatDraftDate(draft.implementedAt)}`}
            description={`Rolled out by ${adminUserName(draft.implementedBy)}.`}
          />
        )}

        <IssueList draft={draft} />

        <Flex gap={8} wrap="wrap">
          {isEditable && (
            <>
              <Button type="secondary" onClick={handleSave} loading={updateMutation.isPending} disabled={isBusy}>
                Save
              </Button>
              <Button type="primary" onClick={handleSubmit} loading={submitMutation.isPending} disabled={isBusy}>
                Submit for review
              </Button>
              <Button type="text" onClick={() => setIsDeleting(true)} disabled={isBusy}>
                Delete draft
              </Button>
            </>
          )}
          {isReviewer && draft.status === CARBON_PROGRAM_DRAFT_STATUS.submitted && (
            <>
              <Button
                type="primary"
                onClick={() => reviewMutation.mutate({ id: draft.id, decision: "approve" })}
                loading={reviewMutation.isPending}
                disabled={isBusy || hasErrors}
              >
                Approve
              </Button>
              <Button type="secondary" onClick={() => setIsRejecting(true)} disabled={isBusy}>
                Request changes
              </Button>
            </>
          )}
          {isReviewer && draft.status === CARBON_PROGRAM_DRAFT_STATUS.approved && (
            <Button type="primary" onClick={() => implementedMutation.mutate(draft.id)} loading={implementedMutation.isPending} disabled={isBusy}>
              Mark as live
            </Button>
          )}
          <Button type="text" onClick={handleCopyConfig}>
            Copy config
          </Button>
        </Flex>

        {changes.length > 0 && (
          <Flex vertical gap={8}>
            <Typography variant="h3">Changes to the live program</Typography>
            <Table
              data={changes.map((change) => ({ ...change, key: change.path }))}
              columns={[
                { title: "setting", dataIndex: "path", key: "path", className: "paragraph-xs-bold" },
                { title: "live", dataIndex: "before", key: "before", render: formatSetting },
                { title: "proposed", dataIndex: "after", key: "after", render: formatSetting },
              ]}
              pagination={false}
            />
          </Flex>
        )}

        <Flex vertical gap={8}>
          <Typography variant="h3">Signed contract</Typography>
          <Flex gap={16} align="center">
            {draft.signedContractUrl ? (
              <Link to={draft.signedContractUrl} target="_blank">
                Download signed contract
              </Link>
            ) : (
              <Typography variant="paragraph-m" color="grey-600">
                No contract uploaded yet.
              </Typography>
            )}
            <Upload
              accept="application/pdf"
              className="auto-height"
              showUploadList={false}
              beforeUpload={(file: File) => {
                uploadContractMutation.mutate({ id: draft.id, file });
                return false;
              }}
            >
              <Button type="secondary" loading={uploadContractMutation.isPending}>
                {draft.signedContractUrl ? "Replace contract" : "Upload contract (PDF)"}
              </Button>
            </Upload>
          </Flex>
        </Flex>

        <DraftForm
          key={draft.updatedAt}
          form={form}
          initialValues={initialValues}
          initialNotes={draft.notes}
          disabled={!isEditable}
          currentPractices={(_.get(draft.config, "fieldsManagement.practices") ?? []) as Practice[]}
          livePrograms={livePrograms}
          partners={partnersQuery.data?.partners ?? []}
          crops={(cropsQuery.data ?? []).map((crop) => ({ id: crop.id, name: crop.name }))}
        />
      </Flex>

      <ModalContent visible={isRejecting} onCancel={() => setIsRejecting(false)}>
        <Flex vertical gap={16}>
          <Typography variant="h3">Request changes</Typography>
          <Form>
            <TextArea name="comment" label="What needs to change?" rows={4} onChange={(event) => setRejectComment(event.target.value)} />
          </Form>
          <Button type="primary" onClick={handleReject} disabled={!rejectComment.trim()} loading={reviewMutation.isPending}>
            Send back to requester
          </Button>
        </Flex>
      </ModalContent>

      <ModalContent visible={isDeleting} onCancel={() => setIsDeleting(false)}>
        <Flex vertical gap={16}>
          <Typography variant="h3">Delete this draft?</Typography>
          <Typography variant="paragraph-m">The draft and its settings are removed. Live programs are not affected.</Typography>
          <Button type="danger" onClick={handleDelete} loading={deleteMutation.isPending}>
            Delete draft
          </Button>
        </Flex>
      </ModalContent>
    </MainPage>
  );
};

export default CarbonProgramDraftPage;
